import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2 } from "lucide-react";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import { User, useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { extractClip } from "data/mutations-common";
import { fetchCredits } from "data/queries-common";
import { useSnackbar } from "notistack";
import {
  JobType,
  UseOverageCreditsCard,
} from "pages/shared/use-overage-credits-card";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toTitleCase } from "utils/commons";
import { resolveIdentityId } from "utils/identity";
import { ClipConfiguration, QuoteSummary } from "../../models";

interface PurchaseWithCreditsProps {
  clipConfig: ClipConfiguration;
  quoteSummary: QuoteSummary;
  planName: string;
  setClipConfig: React.Dispatch<React.SetStateAction<ClipConfiguration>>;
  resetClipConfig: () => void;
  setSubStep: React.Dispatch<React.SetStateAction<number>>;
}

export const PurchaseWithCredits = (props: PurchaseWithCreditsProps) => {
  const [identityState] = useIdentity();
  const identityId = resolveIdentityId(identityState);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { enqueueQueryFailed, enqueueMutationFailed } = useCustomSnackbars();

  const [isEnoughCredits, setIsEnoughCredits] = useState(false);

  const creditsQuery = useQuery({
    queryKey: [QueryKeys.credits, identityId],
    queryFn: () => fetchCredits(),
  });

  useEffect(() => {
    if (creditsQuery.isError) {
      enqueueQueryFailed(creditsQuery.error.toString());
    }
  }, [creditsQuery.isError]);

  const submitExtractJob = useMutation({
    mutationFn: (clipId: string) => extractClip(clipId, "credits"),
    onSuccess: (clipConfig: ClipConfiguration) => {
      (queryClient.invalidateQueries({
        queryKey: [QueryKeys.clipsKey, identityId],
      }),
        enqueueSnackbar("Clip has been submitted for extraction.", {
          variant: "success",
        }));
      props.resetClipConfig();
    },
    onError: (error: Error) => {
      enqueueMutationFailed(error.toString());
    },
  });

  var isCreditsRemaining = false;
  if (creditsQuery.isSuccess && creditsQuery.data) {
    isCreditsRemaining =
      creditsQuery.data.totalCreditsBalance - props.quoteSummary.totalCredits >
      0;
  }

  return (
    <>
      <div className="flex flex-col gap-4 flex-1 min-h-0 overflow-auto *:shrink-0">
        <div className="flex flex-col gap-1 mt-2">
          <Label>Contact Email</Label>
          <Input
            disabled
            value={(identityState as User).email}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label>Contact Phone</Label>
          <Input disabled value="" />
        </div>
        {creditsQuery.isPending && (
          <Skeleton className="h-[100px] w-full" />
        )}

        {creditsQuery.isSuccess && creditsQuery.data && (
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="font-bold px-0 w-3/5">Description</TableHead>
                <TableHead className="font-bold px-0 text-right w-2/5">Credits</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="px-0 whitespace-normal wrap-break-word">
                  {`${toTitleCase(props.planName)} Plan Credits`}
                </TableCell>
                <TableCell className="px-0 text-right">
                  {props.quoteSummary.totalCredits.toLocaleString()}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="px-0 text-right">Credit Balance</TableCell>
                <TableCell className="px-0 text-right">
                  {creditsQuery.data.totalCreditsBalance.toLocaleString()}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="px-0 text-right">Clip Cost</TableCell>
                <TableCell className="px-0 text-right">
                  {props.quoteSummary.totalCredits.toLocaleString()}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="px-0 text-right font-bold">
                  Remaining Balance
                </TableCell>
                <TableCell className="px-0 text-right font-bold">
                  {(
                    creditsQuery.data.totalCreditsBalance -
                    props.quoteSummary.totalCredits
                  ).toLocaleString()}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}

        {!isCreditsRemaining && (
          <UseOverageCreditsCard
            type={JobType.CLIP}
            sx={{
              gridRow: "1",
              gridColumn: "span 2",
              padding: "16px",
              boxShadow: 0,
              border: "1px solid lightgrey",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              overflow: "visible",
            }}
            jobCreditCost={props.quoteSummary.totalCredits}
            isLoading={creditsQuery.isPending}
            setIsEnoughCredits={setIsEnoughCredits}
          />
        )}
      </div>
      <div className="self-end flex pt-2">
        <Button variant="ghost" onClick={() => props.setSubStep(0)}>
          Back
        </Button>
        {creditsQuery.isSuccess && (
          <Button
            disabled={
              (!isEnoughCredits && !isCreditsRemaining) ||
              submitExtractJob.isPending
            }
            onClick={() => {
              submitExtractJob.mutate(props.clipConfig.id);
            }}
          >
            {submitExtractJob.isPending && (
              <Loader2 className="size-4 mr-2 animate-spin" />
            )}
            Purchase
          </Button>
        )}
      </div>
    </>
  );
};
